import {
  Box,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import classNames from "classnames";
import axiosInstant from "../../../../../api";
import {
  addVideoToEmbed,
  removeVideoFromEmbed,
} from "../../../../../api/endpoints";
import { IconCross } from "../../../../../utils/icons";
// eslint-disable-next-line import/no-cycle
import FilterSelector from "../../../videos/adminVideosV2/filterSelector";
import { useFetchVideos } from "../../../videos/hooks/useFetchVideos";
import SingleVideoToSelect from "./SingleVideoToSelect";
import { BootstrapDialog, SubmitBtnStyle } from "./constants";
import { SearchPanel } from "../../../../../components/Header/SearchPanel";
import { DotLottleLoader, LoadMoreBtn } from "../../../../../components/Loader";
import { EmptyPanel } from "../../../../brand/projects/common/EmptyPanel";

interface Props {
  embedId?: number;
  prevSelectedVideos: Array<number>;
  createStory?: () => Promise<number>;
  onCompletion: (success?: boolean) => void;
}

const VideoListingPopup: FC<Props> = ({
  prevSelectedVideos,
  embedId,
  createStory,
  onCompletion,
}) => {
  const {
    videos,
    loading: loadingVideos,
    fetchVideos,
    total,
  } = useFetchVideos("TDC", undefined, true);
  const [selectedVideoFileIds, setSelectedVideoFileIds] = useState<
    Array<number>
  >([]);
  const [loading, setLoading] = useState(false);
  const [showSmallLoader, setShowSmallLoader] = useState(false);
  const [queryFilters, setQueryFilters] = useState<any>({});

  const updateSelectedVideos = (videoFileId: number) => {
    const updatedVideos = [...selectedVideoFileIds];
    const videoIndex = selectedVideoFileIds.findIndex((e) => e === videoFileId);
    if (videoIndex > -1) updatedVideos.splice(videoIndex, 1);
    else updatedVideos.push(videoFileId);
    setSelectedVideoFileIds([...updatedVideos]);
  };

  const addVideoApi = (fileId: number, storyId = 0) =>
    axiosInstant.post(addVideoToEmbed(), { embedId: storyId, fileId });
  const removeVideoApi = (fileId: number, storyId = 0) =>
    axiosInstant.delete(removeVideoFromEmbed(storyId, fileId));

  const loadVideos = (shouldAppend?: boolean, offset = 0) => {
    fetchVideos({ limit: 20, offset, shouldAppend }, { ...queryFilters });
  };

  const updateList = async () => {
    const cleanedPrevFileIds = prevSelectedVideos.filter((e) => e);

    const newlyAddedIds = selectedVideoFileIds.filter(
      (e) => !cleanedPrevFileIds.includes(e)
    );
    const oldRemovedIds = cleanedPrevFileIds.filter(
      (e) => !selectedVideoFileIds.includes(e)
    );

    const selectedVideosList = [...newlyAddedIds, ...oldRemovedIds];
    let storyId = embedId;
    if (selectedVideosList.length) {
      if (!storyId) storyId = await createStory?.();

      if (storyId) {
        setLoading(true);
        const promises = [];
        promises.push(...newlyAddedIds.map((id) => addVideoApi(id, storyId)));
        promises.push(
          ...oldRemovedIds.map((id) => removeVideoApi(id, storyId))
        );

        await Promise.all(promises);
      }
    }
    onCompletion(!!storyId);
  };

  useEffect(() => {
    setShowSmallLoader(false);
    loadVideos();
  }, [queryFilters]);

  useEffect(() => {
    const cleanedFileIds = prevSelectedVideos.filter((e) => e);
    setSelectedVideoFileIds(cleanedFileIds);
  }, []);

  useEffect(() => {
    if (videos.length) setShowSmallLoader(true);
  }, [videos]);

  const shouldHideVideos = !showSmallLoader && (loadingVideos || loading);
  return (
    <BootstrapDialog
      onClose={() => onCompletion(false)}
      aria-labelledby="customized-dialog-title"
      open
    >
      <Box className="flex flex-col sm:flex-row p-6 pb-4 gap-3 sm:gap-6 w-full sm:items-center justify-between">
        <Box className="flex items-center justify-between gap-2 w-full sm:w-auto">
          <Typography fontFamily="CircularStdBlack" fontSize={24} className="whitespace-nowrap">
            Add video to the story
          </Typography>
          <div onClick={() => onCompletion(false)} className="cursor-pointer sm:hidden">
            <IconCross />
          </div>
        </Box>
        <Box className="flex items-center gap-2 w-full sm:w-auto">
          <Box className="flex-1 sm:flex-none">
            <SearchPanel
              onSearchChange={(search) =>
                setQueryFilters((prev: any) => ({ ...prev, search }))
              }
            />
          </Box>
          <div onClick={() => onCompletion(false)} className="cursor-pointer hidden sm:block">
            <IconCross />
          </div>
        </Box>
      </Box>
      <DialogContent dividers className="!p-0">
        <Box className="flex flex-col p-6 pb-4 gap-6 justify-between">
          <FilterSelector
            isStoryVideos
            noTopMargin
            noClearBtn
            queryFilters={queryFilters}
            setQueryFilters={setQueryFilters}
          />
          <Box className="flex gap-0 w-full">
            <Typography className="w-[110px] text-text-2">
              {shouldHideVideos ? 0 : total} Videos
            </Typography>
            <div className="mt-3 border-t w-full border-border-token" />
          </Box>
        </Box>
        {!shouldHideVideos && (
          <Box className="flex flex-col p-6 gap-2">
            {videos.map((e) => (
              <SingleVideoToSelect
                video={e}
                key={e.id}
                isSelected={selectedVideoFileIds.includes(e.id)}
                onClick={updateSelectedVideos}
              />
            ))}
          </Box>
        )}
        {loadingVideos && videos.length === 0 && (
          <DotLottleLoader
            fill={false}
          />
        )}
        {!loadingVideos && !videos.length && (
          <EmptyPanel text="No items found based on your filters." />
        )}
        {Array.isArray(videos) && videos.length < total && (
          <LoadMoreBtn
            customClasses="!mt-1 mb-4"
            loading={loading || loadingVideos}
            onClick={() => loadVideos(true, videos.length)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Box
          className={classNames("flex items-center w-full py-2 px-6", {
            "justify-between": selectedVideoFileIds.length,
            "justify-end": !selectedVideoFileIds.length,
          })}
        >
          {!!selectedVideoFileIds.length && (
            <Typography className="text-accent-text">
              {selectedVideoFileIds.length} videos selected
            </Typography>
          )}
          <Button
            sx={SubmitBtnStyle}
            disabled={loading}
            onClick={updateList}
            style={{ textTransform: "capitalize" }}
          >
            {loading ? "Adding videos ..." : "Add selected videos to story"}
          </Button>
        </Box>
      </DialogActions>
    </BootstrapDialog>
  );
};
export default VideoListingPopup;
